import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  hookAllocationList,
  hookFormList,
  hookIntegrationList,
  hookOfferList,
  hookOrganizationList,
  hookPageList,
  hookPathList,
  hookPostList,
  hookPublisherList,
  hookSiteList,
  hookUserProfileList,
} from '@/stores/base-factories/store-list'
import hookSiteSelectedFactory from '@/stores/base-factories/store-site-selection'
import { TODAY } from '@Components/DateRangeButton'
import app from '@Stores/app'
import createStore from '@Stores/base-factories'
import history from 'utils/history'

import { buildChartQuery } from './utils/chartQuery'
import { nextChartId } from './utils/chartUtils'
import { pruneGroupbyForCollection } from './utils/collectionGroupby'
import {
  createDefaultReportConfig,
  normalizeMetrics,
} from './utils/constants'
import { readDefaultPins, writeDefaultPins } from './utils/defaultReportPins'
import {
  DEFAULT_PINNED_REPORT_IDS,
  DEFAULT_REPORT_VIEWS,
  createDefaultReportCharts,
  createDefaultReportConfigForCollection,
  isDefaultReportId,
} from './utils/defaultReports'
import {
  filtersToConditions,
  filtersToFilterby,
  normalizeFilterby,
  pruneFiltersForCollection,
} from './utils/filterEntities'
import useFilterEntityListsFactory from './utils/filterEntityLists'
import {
  applyReportViewConfiguration,
  buildReportViewConfiguration,
} from './utils/reportViewConfig'
import { enrichCustomReportData } from './utils/tableUtils'

const AUTO_RUN_DEBOUNCE = 350

const serializeDate = date => date?.valueOf?.() ?? date ?? null

const useCustomReportFactory = ({ fetch, siteSelected }) => {
  const [config, setConfig] = useState(() => createDefaultReportConfig(TODAY))
  const [result, setResult] = useState({ table: null, charts: {}, status: 'idle', error: null })
  const [charts, setCharts] = useState([])
  const [activeViewId, setActiveViewId] = useState(null)
  const [autoRunEnabled, setAutoRunEnabled] = useState(false)

  const updateConfig = useCallback(patch => {
    setConfig(prev => {
      const next = { ...prev, ...patch }

      if (patch.collection && patch.collection !== prev.collection) {
        next.filters = pruneFiltersForCollection(next.filters, patch.collection)
        next.groupby = pruneGroupbyForCollection(next.groupby, patch.collection)
      }

      return next
    })
  }, [])

  const setDates = useCallback(({ startDate, endDate }) => {
    setConfig(prev => ({ ...prev, startDate, endDate }))
  }, [])

  const resetConfig = useCallback(() => {
    setConfig(createDefaultReportConfig(TODAY))
    setResult({ table: null, charts: {}, status: 'idle', error: null })
    setCharts([])
    setActiveViewId(null)
  }, [])

  const applyConfiguration = useCallback(configuration => {
    applyReportViewConfiguration(
      configuration,
      { startDate: config.startDate, endDate: config.endDate },
      setConfig,
      setCharts,
    )
  }, [config.startDate, config.endDate])

  const buildConfiguration = useCallback(() => buildReportViewConfiguration({ config, charts })
  , [config, charts])

  const addChart = useCallback(chartConfig => {
    setCharts(prev => [...prev, { ...chartConfig, id: nextChartId(prev) }])
  }, [])

  const updateChart = useCallback((chartId, patch) => {
    setCharts(prev => prev.map(chart => chart.id === chartId ? { ...chart, ...patch } : chart,
    ))
  }, [])

  const removeChart = useCallback(chartId => {
    setCharts(prev => prev.filter(chart => chart.id !== chartId))
  }, [])

  const buildRequest = useCallback(() => {
    const {
      collection,
      groupby,
      metrics,
      sortby,
      filters,
      startDate,
      endDate,
    } = config

    const conditions = filtersToConditions(filters || [])

    return {
      collection,
      startDate,
      endDate,
      filterby: normalizeFilterby(filtersToFilterby(filters || [])),
      ...conditions.length ? { conditions } : {},
      table: {
        groupby,
        metrics: normalizeMetrics(metrics),
        sortby,
      },
      charts: charts.map(chart => buildChartQuery(chart, config)).filter(Boolean),
    }
  }, [charts, config])

  const postReport = useCallback(async request => {
    const { id: siteId, key } = siteSelected || {}
    if (!siteId || !request) return null

    const { startDate, endDate, ...rest } = request

    return fetch({
      url: `reports/site/${siteId}/custom`,
      method: 'post',
      data: {
        ...rest,
        startDate: startDate.tz(app.timezone, true).format(),
        endDate: endDate.tz(app.timezone, true).format(),
        [key]: siteId,
      },
    })
  }, [fetch, siteSelected])

  const requestSignature = useMemo(() => {
    const request = buildRequest()
    if (!request.table.groupby?.length || !request.table.metrics?.length) return null

    return JSON.stringify({
      ...request,
      startDate: serializeDate(request.startDate),
      endDate: serializeDate(request.endDate),
      siteId: siteSelected?.id ?? null,
    })
  }, [buildRequest, siteSelected?.id])

  const lastRunSignatureRef = useRef(null)
  const requestIdRef = useRef(0)

  const fetchReport = useCallback(async signature => {
    const request = buildRequest()
    if (!request.table.groupby?.length || !request.table.metrics?.length) return

    lastRunSignatureRef.current = signature ?? null
    requestIdRef.current += 1
    const requestId = requestIdRef.current

    setResult(prev => ({ ...prev, status: 'loading', error: null }))

    try {
      const response = await postReport(request)
      const table = await enrichCustomReportData(response?.table ?? response, request.table.groupby)

      const chartEntries = await Promise.all(request.charts.map(async query => {
        const data = await enrichCustomReportData(response?.charts?.[query.id], query.groupby)
        return [query.id, { data, groupby: query.groupby }]
      }))

      if (requestIdRef.current !== requestId) return

      setResult({
        table,
        charts: Object.fromEntries(chartEntries),
        status: 'loaded',
        error: null,
      })
    } catch (error) {
      if (requestIdRef.current !== requestId) return
      setResult({ table: null, charts: {}, status: 'error', error })
    }
  }, [buildRequest, postReport])

  const runReport = useCallback(async () => {
    await fetchReport(requestSignature)
  }, [fetchReport, requestSignature])

  useEffect(() => {
    if (!autoRunEnabled || !requestSignature) return
    if (lastRunSignatureRef.current === requestSignature) return

    setResult(prev => {
      if (prev.status === 'loading') return prev
      return { ...prev, status: 'loading', error: null }
    })

    const timeout = setTimeout(() => fetchReport(requestSignature), AUTO_RUN_DEBOUNCE)

    return () => clearTimeout(timeout)
  }, [autoRunEnabled, requestSignature, fetchReport])

  const isLoading = result.status === 'loading'

  const list = useCallback(async () => {
    await runReport()
  }, [runReport])

  return useMemo(() => ({
    config: {
      value: config,
      update: updateConfig,
      setDates,
      reset: resetConfig,
    },
    table: {
      data: result.table,
      status: result.status,
      error: result.error,
    },
    charts: {
      items: charts,
      data: result.charts,
      add: addChart,
      update: updateChart,
      remove: removeChart,
      set: setCharts,
    },
    activeView: {
      id: activeViewId,
      setId: setActiveViewId,
      apply: applyConfiguration,
      build: buildConfiguration,
    },
    report: {
      list,
      run: runReport,
      isLoading,
      setAutoRun: setAutoRunEnabled,
    },
  }), [
    config,
    updateConfig,
    setDates,
    resetConfig,
    result,
    charts,
    addChart,
    updateChart,
    removeChart,
    activeViewId,
    applyConfiguration,
    buildConfiguration,
    list,
    runReport,
    isLoading,
  ])
}

const useCustomReportViewActions = ({ reportView, activeView }) => {
  const submit = reportView.useSubmit()

  const defaultPinsRef = useRef(readDefaultPins())
  const [defaultPins, setDefaultPins] = useState(defaultPinsRef.current)

  const defaults = useMemo(() => {
    const pinnedIds = defaultPins || DEFAULT_PINNED_REPORT_IDS

    return DEFAULT_REPORT_VIEWS.map(view => ({
      ...view,
      configuration: {
        ...view.configuration,
        pinned: pinnedIds.includes(view.id),
      },
    }))
  }, [defaultPins])

  const items = useMemo(
    () => [...defaults, ...reportView.items],
    [defaults, reportView.items],
  )

  const setDefaultPinned = useCallback((viewId, pinned) => {
    const current = defaultPinsRef.current || DEFAULT_PINNED_REPORT_IDS
    if (current.includes(viewId) === pinned) return

    const next = pinned ? [...current, viewId] : current.filter(id => id !== viewId)

    defaultPinsRef.current = next
    writeDefaultPins(next)
    setDefaultPins(next)
  }, [])

  const save = useCallback(async () => {
    if (isDefaultReportId(activeView.id)) return

    const view = reportView.items.find(item => item.id === activeView.id)
    if (!view) return

    await submit({
      id: view.id,
      description: view.description,
      configuration: {
        ...activeView.build(),
        pinned: view.configuration?.pinned ?? false,
      },
    })
    await reportView.list()
  }, [activeView, reportView, submit])

  const setPinned = useCallback(async (viewId, pinned) => {
    if (isDefaultReportId(viewId)) {
      setDefaultPinned(viewId, pinned)
      return
    }

    const view = reportView.items.find(item => item.id === viewId)
    if (!view) return

    await submit({
      id: view.id,
      description: view.description,
      configuration: {
        ...view.configuration,
        pinned,
      },
    })
    await reportView.list()
  }, [reportView, setDefaultPinned, submit])

  const create = useCallback(async (description, collection) => {
    const defaultConfig = createDefaultReportConfigForCollection(collection, TODAY)
    const configuration = buildReportViewConfiguration({
      config: defaultConfig,
      charts: createDefaultReportCharts(collection),
      pinned: true,
    })

    const result = await submit({ description, configuration })
    await reportView.list()

    const createdId = result?.id || reportView.items.find(item => item.description === description)?.id
    if (createdId) {
      history.push(`/reports/custom/${createdId}`)
    }

    return createdId
  }, [submit, reportView])

  return useMemo(() => ({
    views: {
      items,
      defaults,
    },
    activeView: {
      ...activeView,
      isDefault: isDefaultReportId(activeView.id),
      save,
      create,
      setPinned,
    },
  }), [items, defaults, activeView, save, create, setPinned])
}

const { Provider, useStore } = createStore()
  .extend(hookSiteSelectedFactory('siteId'))
  .extend(hookAllocationList())
  .extend(hookPathList())
  .extend(hookPageList())
  .extend(hookPublisherList())
  .extend(hookOfferList())
  .extend(hookIntegrationList())
  .extend(hookFormList())
  .extend(hookOrganizationList())
  .extend(hookSiteList())
  .extend(hookUserProfileList('userProfiles'))
  .extend(hookPostList('landers'))
  .extend(useFilterEntityListsFactory)
  .extendReportFactory()
  .extend(useCustomReportFactory)
  .extendReportViewFactory('custom')
  .extend(useCustomReportViewActions)
  .factory()

export {
  Provider as CustomReportProvider,
  useStore as useCustomReportStore,
}
